import { MouseEvent, useContext, useCallback } from 'react';
import { OwApp } from '../interfaces/domain/ow-app.modal';
import { useAnalytics } from './use-analytics';
import { useOS } from './use-os';
import { LinkType, useDeepLink } from './use-deep-link';
import { getQueryParam } from './use-get-query-param';
import { AppBusEventType } from '../interfaces/bus-events';
import { OwAppTile } from '../interfaces/domain/ow-app-tile.modal';
import { useUserProfile } from '../context/user-profile-context';
import { dispatch } from './use-bus';
import Utils from '../utils/utils';
import GlobalContext from '../context/global-context';
import { useOverwolfStore } from './use-overwolf-store';
import { useInstalledExtensions } from './use-installed-extensions';
import type {
  OverwolfInstallOrigin,
  OverwolfUtmParams,
} from '@ow/shared/store-bridge/bridge-channel';

// Identifies which UI triggered the download click, both for analytics
// (sent verbatim as the `component` field) and to derive origin.isOneApp /
// origin.fromCarousel below. Values are the pre-existing analytics strings —
// keep them unchanged or historical tracking data will fork.
export enum DownloadClickComponent {
  OneApp = 'App one page',
  ShelveTile = 'App shelve tile',
  Login = 'Login',
  DownloadModal = 'Download Modal',
  PageHeader = 'page header',
}

interface UseDownloadClickHandlerProps {
  item: OwApp | OwAppTile;
  component: DownloadClickComponent;
  enableDeepLink?: boolean;
  // Name of the shelf this download was triggered from (shelf tiles only) —
  // forwarded as origin.shelf on the native install call.
  shelf?: string;
}

// Query param the carousel appends to its CTA link (see
// carousel-image-slider-item.tsx) so the app page it lands on knows the visit
// came from a carousel slide click.
const CAROUSEL_ORIGIN_PARAM = 'origin';
const CAROUSEL_ORIGIN_VALUE = 'carousel';

// Maps each UTM query param to its key on OverwolfUtmParams (matching the
// native URLParams DTO's JSON property names).
const UTM_PARAM_KEYS: Record<string, keyof OverwolfUtmParams> = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_term: 'term',
  utm_content: 'content',
};

// Reads whichever UTM params are present on the given query string. Returns
// undefined (rather than an empty object) when none are present, so callers
// can omit the key entirely.
const getUtmParams = (search: string): OverwolfUtmParams | undefined => {
  const utmParams: OverwolfUtmParams = {};
  for (const [param, key] of Object.entries(UTM_PARAM_KEYS)) {
    const value = getQueryParam(search, param);
    if (value) {
      utmParams[key] = value;
    }
  }
  return Object.keys(utmParams).length ? utmParams : undefined;
};

// Overwrites utm_source on the Electron installer URL, regardless of what the
// page that built it carried (it defaults to the web store's source). Only
// called on the isOverwolfStoreAvailable + isClickedElectron path below, which
// is reachable only from inside the Overwolf client, so the download is always
// attributable to the client store, never the plain web store.
const withClientAppStoreSource = (url: string): string => {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('utm_source', 'client_app_store');
    return parsed.toString();
  } catch {
    return url;
  }
};

// -----------------------------------------------------------------------------
export const useDownloadClickHandler = ({
  item,
  component,
  enableDeepLink = true,
  shelf,
}: UseDownloadClickHandlerProps) => {
  const { config } = useContext(GlobalContext);
  const userProfile = useUserProfile();
  const analytics = useAnalytics();
  const { downloadApp } = useDeepLink();
  const OS = useOS();
  const {
    isAvailable: isOverwolfStoreAvailable,
    installApp,
    launchApp,
    launchElectronApp,
    downloadApp: downloadElectronApp,
  } = useOverwolfStore();
  const { installedAppIds, installedElectronAppIds, markInstalling } =
    useInstalledExtensions();

  const enableDeepLinking =
    (config?.publicRuntimeConfig.adminParams?.ENABLE_DEEP_LINKING &&
      enableDeepLink) ??
    false;

  const enableEmbedHashes =
    config?.publicRuntimeConfig.adminParams?.ENABLE_EMBED_HASHES ?? false;

  const enableLoginOnDownload =
    config?.publicRuntimeConfig.adminParams?.ENABLE_LOGIN_ON_DOWNLOAD ?? false;

  // ---------------------------------------------------------------------------
  const appendHashedEmails = useCallback(
    (url: string): string => {
      try {
        if (!enableEmbedHashes) {
          return url;
        }

        if (
          !userProfile.isLoggedIn ||
          !userProfile.currentUser?.profile?.email
        ) {
          return url;
        }

        const { md5, sha256, sha1 } = userProfile.getHashedEmailObject();
        const urlObj = new URL(url.toString());

        if (md5 && !urlObj.searchParams.has('eMd5')) {
          urlObj.searchParams.append('eMd5', md5);
        }

        if (sha1 && !urlObj.searchParams.has('eSha1')) {
          urlObj.searchParams.append('eSha1', sha1);
        }

        if (sha256 && !urlObj.searchParams.has('eSha256')) {
          urlObj.searchParams.append('eSha256', sha256);
        }

        return urlObj.toString();
      } catch (error) {
        return url;
      }
    },
    [userProfile],
  );

  // ---------------------------------------------------------------------------
  const trackDownloadClicked = useCallback(
    (
      app: OwApp | OwAppTile,
      appId: string,
      linkType: LinkType,
    ): void => {
      analytics.track('appstore_download_clicked', {
        appId,
        appName: app?.title ?? 'n/a',
        component: component,
        linkType,
        autoDownload: Boolean(getQueryParam(location.search, 'startdownload')),
      });

      analytics.gtagPush({
        event: 'app_download_cta',
        app_name: app?.title ?? 'n/a',
        app_id: appId,
        app_os: OS,
      });
    },
    [analytics, component, OS],
  );

  // ---------------------------------------------------------------------------
  // Tags the native install call with where it was triggered from: the current
  // page path, whether the app page was reached via a carousel slide, whether
  // the trigger is the app detail page itself (vs. a shelf tile), and the shelf
  // name when applicable.
  const buildInstallOrigin = useCallback((): OverwolfInstallOrigin => {
    const isOneApp = component === DownloadClickComponent.OneApp;
    const utmParams = getUtmParams(location.search);
    return {
      page: window.location.pathname,
      isOneApp,
      fromCarousel:
        isOneApp &&
        getQueryParam(location.search, CAROUSEL_ORIGIN_PARAM) ===
          CAROUSEL_ORIGIN_VALUE,
      ...(shelf ? { shelf } : {}),
      ...(utmParams ? { utmParams } : {}),
    };
  }, [component, shelf]);

  const showLoginBeforeDownload = (skipLoginPrompt: boolean): boolean => {
    if (window.self !== window.top) {
      return false;
    }

    if (!enableLoginOnDownload) {
      return false;
    }

    if (skipLoginPrompt) {
      return false;
    }

    if (userProfile?.isLoggedIn) {
      return false;
    }

    return true;
  };

  // ---------------------------------------------------------------------------
  const handleClickDownload = useCallback(
    async (
      event: MouseEvent<HTMLButtonElement>,
      overrideUrl: string = null,
      skipLoginPrompt: boolean = false,
      overrideItem: OwApp | OwAppTile = null,
    ): Promise<void> => {
      const app = overrideItem ?? item;

      if (showLoginBeforeDownload(skipLoginPrompt)) {
        event?.preventDefault();
        const sanitizeComponent = component
          .replace(/[^a-zA-Z0-9_-]/g, '_')
          .toLowerCase();

        dispatch({
          type: AppBusEventType.App.ShowLoginModal,
          data: {
            show: true,
            overrideUrl,
            source: `download_${sanitizeComponent}`,
            app,
          },
        });
        return;
      }

      let urlToUse = overrideUrl.toString();
      const mainDownloadExtensionId =
        app?.alternativeDownloads?.[0]?.appId ?? app?.id ?? null;

      const clickedDownload = app?.alternativeDownloads?.find(
        (d) => d.appId && overrideUrl?.includes(d.appId),
      );
      const isClickedElectron =
        clickedDownload?.isElectron ?? app?.isElectron;
      const clickedAppId = clickedDownload?.appId ?? app?.id;

      let downloadingMainApp = false;
      if (overrideUrl.includes(app?.id)) {
        downloadingMainApp = true;
      }

      // Inside the Overwolf client (embedded in an iframe) the install / launch
      // is handled by the native client APIs rather than deep links or browser
      // navigation. We still report the same analytics, but skip the download
      // modal and any URL navigation.
      if (isOverwolfStoreAvailable && !isClickedElectron && clickedAppId) {
        event?.preventDefault();

        if (installedAppIds.has(clickedAppId)) {
          launchApp(clickedAppId);
        } else {
          installApp(clickedAppId, buildInstallOrigin());
          // Show the in-progress indicator until the app is marked installed.
          markInstalling(clickedAppId);
        }

        trackDownloadClicked(app, mainDownloadExtensionId, 'overwolfStore');

        return;
      }

      // Overwolf-Electron app inside the client. If it's already installed,
      // launch its .exe; otherwise hand the installer URL to the shell, which
      // downloads it (to the Downloads folder) and launches it once finished.
      // Either way skip the download modal and any browser/deep-link navigation.
      if (isOverwolfStoreAvailable && isClickedElectron && clickedAppId) {
        event?.preventDefault();

        if (installedElectronAppIds.has(clickedAppId)) {
          launchElectronApp(clickedAppId);
        } else {
          let electronUrl = Utils.appendHTTPSIfMissing(overrideUrl.toString());
          electronUrl = withClientAppStoreSource(electronUrl);
          electronUrl = appendHashedEmails(electronUrl);
          downloadElectronApp(electronUrl, clickedAppId, app?.title);
        }

        trackDownloadClicked(app, mainDownloadExtensionId, 'overwolfStore');
        return;
      }

      urlToUse = Utils.appendHTTPSIfMissing(urlToUse);
      urlToUse = Utils.InstallerDownloadOverwrite(
        event,
        mainDownloadExtensionId,
        urlToUse,
      );

      urlToUse = appendHashedEmails(urlToUse);
      let linkType: LinkType = 'standard';
      if (
        app &&
        enableDeepLinking &&
        !isClickedElectron &&
        (downloadingMainApp || !!clickedDownload?.appId)
      ) {
        event?.preventDefault();
        const deepLinkResult = await downloadApp(urlToUse, clickedAppId);
        linkType = deepLinkResult.linkType;
        urlToUse = deepLinkResult.url;
      } else {
        const target = event?.target as HTMLElement;
        target?.setAttribute('href', urlToUse);
      }

      trackDownloadClicked(app, mainDownloadExtensionId, linkType);

      dispatch({
        type: AppBusEventType.App.ShowDownloadModal,
        data: {
          isOpen: true,
          currentApp: app ?? null,
          url: overrideUrl.toString(),
          isDeepLink: linkType === 'deepLink',
        },
      });

      if (event === null && linkType !== 'deepLink') {
        window.location.href = urlToUse;
      }
    },

    [
      userProfile,
      item,
      component,
      isOverwolfStoreAvailable,
      installedAppIds,
      installedElectronAppIds,
      installApp,
      launchApp,
      launchElectronApp,
      downloadElectronApp,
      markInstalling,
      trackDownloadClicked,
      buildInstallOrigin,
    ],
  );

  // ---------------------------------------------------------------------------

  return { handleClickDownload };
};
